import type {
  CreditParameterCatalog,
  CreditParameterDiff,
  CreditParameterSet,
  CreditParameterSpec,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import {
  BRL,
  formatDateTime,
  moneyInputError,
  moneyToInput,
  parseMoney,
} from '@/shared/lib/format';

/**
 * Leitura, comparacao e texto dos valores de parametro do motor de credito.
 *
 * Os valores chegam no JSON gravado da versao: dinheiro em centavos com moeda e
 * expoente, fator em milesimos, nulo como nulo. A tela nunca faz conta com eles: so
 * le o que a pessoa digitou, confere e devolve no mesmo formato. Em branco e nulo, e
 * nunca zero.
 */

/** O resultado de um campo: o valor pronto para o servidor, ou o erro a mostrar. */
export type Resultado = { ok: true; valor: unknown } | { ok: false; erro: string };

export function valido(valor: unknown): Resultado {
  return { ok: true, valor };
}

export function invalido(erro: string): Resultado {
  return { ok: false, erro };
}

/**
 * JSON com as chaves de objeto ordenadas, para comparar dois valores sem depender da
 * ordem em que as chaves chegaram. O servidor serializa os mapas com as chaves
 * ordenadas; os objetos de estrutura saem na ordem dos campos, e a ordem nao muda o
 * valor.
 */
export function jsonCanonico(valor: unknown): string {
  return JSON.stringify(ordenar(valor));
}

function ordenar(valor: unknown): unknown {
  if (Array.isArray(valor)) return valor.map(ordenar);
  if (valor !== null && typeof valor === 'object') {
    const objeto = valor as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(objeto)
        .sort()
        .map((chave) => [chave, ordenar(objeto[chave])]),
    );
  }
  return valor;
}

export function iguais(a: unknown, b: unknown): boolean {
  return jsonCanonico(a ?? null) === jsonCanonico(b ?? null);
}

/** Dinheiro no formato do contrato. */
export function ehDinheiro(valor: unknown): valor is Money {
  if (valor === null || typeof valor !== 'object') return false;
  const v = valor as Record<string, unknown>;
  return (
    typeof v.amount === 'number' &&
    typeof v.currency === 'string' &&
    typeof v.exponent === 'number'
  );
}

/**
 * Texto de edicao do dinheiro. E o `moneyToInput` corrigido do SC-R21 (virgula
 * decimal, sem milhar), com uma diferenca so: zero sai "0". O campo vazio aqui e
 * "em branco", e um teto zero nao pode reabrir como teto em branco.
 */
export function textoDoDinheiro(valor: Money | null | undefined): string {
  if (!valor) return '';
  if (valor.amount === 0) return '0';
  return moneyToInput(valor);
}

/**
 * Le o dinheiro digitado. Vazio e nulo so quando o parametro aceita em branco; no
 * parametro que nao aceita, vazio e erro, e nunca zero.
 */
export function lerDinheiro(
  texto: string,
  aceitaEmBranco: boolean,
  currency: string = BRL,
): Resultado {
  if (texto.trim() === '') {
    return aceitaEmBranco ? valido(null) : invalido('Informe o valor.');
  }
  const erro = moneyInputError(texto, currency);
  if (erro) return invalido(erro);
  const lido = parseMoney(texto, currency);
  return lido ? valido(lido) : invalido('Valor invalido.');
}

/** Le um inteiro exato. Casa decimal e recusada, e nao arredondada. */
export function lerInteiro(texto: string, aceitaEmBranco: boolean): Resultado {
  const limpo = texto.trim();
  if (limpo === '') {
    return aceitaEmBranco ? valido(null) : invalido('Informe um numero inteiro.');
  }
  if (!/^-?\d+$/.test(limpo)) {
    return invalido('Use um numero inteiro, sem casas decimais e sem separadores.');
  }
  const numero = Number(limpo);
  if (!Number.isSafeInteger(numero)) return invalido('Numero grande demais.');
  return valido(numero);
}

/**
 * Le a estrutura digitada em JSON. Todo numero precisa ser inteiro exato: o motor
 * guarda dinheiro em centavos e fator em milesimos, e o servidor recusa casa decimal.
 */
export function lerJson(texto: string): Resultado {
  let lido: unknown;
  try {
    lido = JSON.parse(texto);
  } catch {
    return invalido('JSON invalido. Confira aspas, virgulas e chaves.');
  }
  const decimal = primeiroNumeroNaoInteiro(lido);
  if (decimal !== undefined) {
    return invalido(
      `Numero ${decimal} nao e inteiro. Dinheiro vai em centavos e fator em milesimos.`,
    );
  }
  return valido(lido);
}

function primeiroNumeroNaoInteiro(valor: unknown): number | undefined {
  if (typeof valor === 'number') {
    return Number.isSafeInteger(valor) ? undefined : valor;
  }
  if (Array.isArray(valor)) {
    for (const item of valor) {
      const achado = primeiroNumeroNaoInteiro(item);
      if (achado !== undefined) return achado;
    }
    return undefined;
  }
  if (valor !== null && typeof valor === 'object') {
    for (const item of Object.values(valor)) {
      const achado = primeiroNumeroNaoInteiro(item);
      if (achado !== undefined) return achado;
    }
  }
  return undefined;
}

/** Nome legivel de um valor de dominio, ou o proprio valor. */
export function rotuloDe(valor: string, rotulos: Record<string, string>): string {
  return rotulos[valor] ?? valor;
}

/** Nome da classificacao da fonte, como a skill de seguro garantia os define. */
export function rotuloDaClassificacao(classificacao: string | undefined): string {
  // A decisao do dono pode vir com a referencia: "DECISAO DO DONO (missao de ...)".
  if (classificacao?.startsWith('DECISAO DO DONO')) return 'Decisao do dono';
  switch (classificacao) {
    case 'DNH':
    case 'DOCUMENTADA NAO HOMOLOGADA':
      return 'Documentada nao homologada';
    case 'CONFLITANTE':
      return 'Conflitante';
    case 'PENDENTE':
      return 'Pendente';
    case 'PREMISSA':
      return 'Premissa';
    case 'DECISAO DO DONO':
      return 'Decisao do dono';
    case 'CONFIRMADA':
      return 'Confirmada';
    default:
      return classificacao ?? 'Sem classificacao';
  }
}

export type TomDaClassificacao = 'info' | 'warning' | 'danger' | 'neutral' | 'brand';

export function tomDaClassificacao(
  classificacao: string | undefined,
): TomDaClassificacao {
  if (classificacao?.startsWith('DECISAO DO DONO')) return 'brand';
  switch (classificacao) {
    case 'CONFLITANTE':
      return 'warning';
    case 'PENDENTE':
      return 'danger';
    case 'PREMISSA':
      return 'neutral';
    case 'DECISAO DO DONO':
    case 'CONFIRMADA':
      return 'brand';
    default:
      return 'info';
  }
}

/** Situacao como a tela mostra: a ativa com vigencia futura esta agendada. */
export type SituacaoDaVersao = 'rascunho' | 'agendada' | 'ativa' | 'revogada';

export function situacaoDe(
  versao: Pick<CreditParameterSet, 'status' | 'effective_from'>,
  agora: number = Date.now(),
): SituacaoDaVersao {
  if (versao.status === 'agendada') return 'agendada';
  if (
    versao.status === 'ativa' &&
    versao.effective_from &&
    Date.parse(versao.effective_from) > agora
  ) {
    return 'agendada';
  }
  return versao.status;
}

export const rotuloDaSituacao: Record<SituacaoDaVersao, string> = {
  rascunho: 'Rascunho',
  agendada: 'Agendada',
  ativa: 'Ativa',
  revogada: 'Revogada',
};

/** De onde a versao veio, em texto. */
export function rotuloDaOrigem(versao: CreditParameterSet): string {
  switch (versao.source) {
    case 'carga_inicial':
      return 'Carga inicial dos valores documentados';
    case 'copia_da_ativa':
      return versao.based_on_version
        ? `Copia da versao ${versao.based_on_version}`
        : 'Copia da versao ativa';
    case 'valores_documentados':
      return 'Valores documentados';
    case 'padrao_global':
    case 'copia_do_padrao_global':
      return versao.based_on_global_version
        ? `Copia da versao ${versao.based_on_global_version} do padrao global`
        : 'Copia do padrao global';
    case 'heranca_padrao_global':
      return versao.based_on_global_version
        ? `Heranca da versao ${versao.based_on_global_version} do padrao global`
        : 'Heranca do padrao global';
    default:
      return versao.source || 'Nao informada';
  }
}

/** Por que a versao foi revogada, em texto. Vazio fora da revogada. */
export function rotuloDaRevogacao(
  versao: Pick<CreditParameterSet, 'revocation_kind'>,
): string {
  switch (versao.revocation_kind) {
    case 'substituida':
      return 'Substituida por outra versao';
    case 'agendamento_cancelado':
      return 'Agendamento cancelado antes da data';
    default:
      return '';
  }
}

/** Quantas aprovacoes, em texto. */
export function textoDasAprovacoes(total: number): string {
  return total === 1 ? '1 aprovacao registrada' : `${total} aprovacoes registradas`;
}

/**
 * Comparacao feita na tela, com os valores das duas versoes ja lidos.
 *
 * Serve ao escopo cuja rota de comparacao ainda nao existe (padrao global antes da
 * governanca). E a mesma comparacao do servidor: todos os parametros, na ordem do
 * catalogo, pelo JSON canonico.
 */
export function compararLocalmente(
  catalogo: CreditParameterCatalog,
  base: CreditParameterSet,
  outra: CreditParameterSet,
): CreditParameterDiff {
  const valoresBase = base.values ?? {};
  const valoresOutra = outra.values ?? {};
  const descritos = new Map<string, CreditParameterSpec>(
    catalogo.parameters.map((spec) => [spec.key, spec]),
  );
  const chaves = [
    ...catalogo.parameters.map((spec) => spec.key),
    ...[...new Set([...Object.keys(valoresBase), ...Object.keys(valoresOutra)])]
      .filter((chave) => !descritos.has(chave))
      .sort(),
  ].filter((chave) => chave in valoresBase || chave in valoresOutra);

  const parameters = chaves.map((key) => {
    const spec = descritos.get(key);
    const a = valoresBase[key] ?? null;
    const b = valoresOutra[key] ?? null;
    return {
      key,
      group: spec?.group ?? '',
      label: spec?.label ?? key,
      base: a,
      other: b,
      changed: !iguais(a, b),
    };
  });
  const ref = (v: CreditParameterSet) => ({
    id: v.id,
    version: v.version,
    status: v.status,
    values_hash: v.values_hash,
  });
  return {
    base: ref(base),
    other: ref(outra),
    changed_count: parameters.filter((p) => p.changed).length,
    parameters,
  };
}

/**
 * O instante ainda nao chegou. Serve so a tela (o texto do botao e a dica da
 * vigencia): quem decide se a versao fica agendada e o servidor, pelo relogio dele.
 */
export function noFuturo(instante: Date, agora: number = Date.now()): boolean {
  return instante.getTime() > agora;
}

/**
 * Aviso depois da ativacao: ativa agora, agendada, ou aprovacao registrada que ainda
 * espera outra pessoa (a plataforma exige mais de um aprovador).
 */
export function avisoDaAtivacao(versao: CreditParameterSet): string {
  if (versao.status === 'rascunho') {
    return `Aprovacao registrada na versao ${versao.version} (${textoDasAprovacoes(versao.approvals.length)}). A versao entra em vigor com a aprovacao de outra pessoa.`;
  }
  if (situacaoDe(versao) === 'agendada' && versao.effective_from) {
    return `Versao ${versao.version} agendada para ${formatDateTime(versao.effective_from)}.`;
  }
  return `Versao ${versao.version} ativada.`;
}

const rotuloDaDisponibilidade: Record<string, string> = {
  dados_do_sistema: 'Decide com os dados do sistema',
  depende_de_consulta: 'Depende de consulta externa',
  depende_de_limite: 'Depende da estrutura de limite',
};

/** O que a regra consegue decidir hoje, em texto. */
export function textoDaDisponibilidade(disponibilidade: string): string {
  return rotuloDaDisponibilidade[disponibilidade] ?? disponibilidade;
}
